import { useState, useEffect } from 'react';
import { db } from '../../lib/firebase';
import { collection, getDocs, doc, updateDoc } from 'firebase/firestore';
import { X } from 'lucide-react';
import { toast } from 'react-toastify';

interface Category {
  id: string;
  name: string;
  type: string;
}

interface BulkUpdateModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedProducts: string[];
  onUpdate: () => void;
}

export default function BulkUpdateModal({ isOpen, onClose, selectedProducts, onUpdate }: BulkUpdateModalProps) {
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      const categoriesRef = collection(db, 'categories');
      const snapshot = await getDocs(categoriesRef);
      const categoriesData = snapshot.docs.map(doc => ({
        id: doc.id,
        name: doc.data().name,
        type: doc.data().type || 'normal'
      }));
      setCategories(categoriesData);
    } catch (err) {
      console.error('Erro ao carregar categorias:', err);
      toast.error('Erro ao carregar categorias');
    }
  };

  const handleUpdate = async () => {
    if (!selectedCategory) {
      toast.error('Selecione uma categoria');
      return;
    }

    try {
      setLoading(true);
      
      // Atualizar cada produto selecionado
      await Promise.all(
        selectedProducts.map(async (productId) => {
          const productRef = doc(db, 'products', productId);
          await updateDoc(productRef, {
            category: selectedCategory,
            lastUpdate: new Date().toISOString()
          });
        })
      );

      toast.success('Produtos atualizados com sucesso!');
      onUpdate();
      onClose();
    } catch (err) {
      console.error('Erro ao atualizar produtos:', err);
      toast.error('Erro ao atualizar produtos');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">
            Atualizar {selectedProducts.length} produto{selectedProducts.length !== 1 ? 's' : ''}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6">
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nova Categoria
            </label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="">Selecione uma categoria</option>
              {categories.map(category => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 bg-gray-50 rounded-b-lg">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
          >
            Cancelar
          </button>
          <button
            onClick={handleUpdate}
            disabled={loading || !selectedCategory}
            className={`px-4 py-2 text-sm font-medium text-white bg-primary rounded-md hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary ${
              (loading || !selectedCategory) ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            {loading ? 'A atualizar...' : 'Atualizar'}
          </button>
        </div>
      </div>
    </div>
  );
}
